import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import MainInput from "../../Elements/MainInput/MainInput";
import SecondButton from "../../Elements/SecondButton/SecondButton";
import MainButton from "../../Elements/MainButton/MainButton";
import RefundModal from "../RefundModal/RefundModal";
import Switch from "../../Elements/Switch/Switch";

import { fetchDisableInstructions, updateAutoTopup } from "../../../services/EsimService";
import copyIcon from "../../../assets/icons/common/copy-alt.png";
import "./EsimActions.css";

const EsimActions = ({ selectedEsim, deviceType, onToggleTopUp, isTopUpOpen, hasTopUpPackages }) => {
  const { t } = useTranslation();
  const [copiedField, setCopiedField] = useState(null);

  const [disableSteps, setDisableSteps] = useState([]);
  const [loadingDisable, setLoadingDisable] = useState(false);

  const [isAutoRenew, setIsAutoRenew] = useState(selectedEsim?.automatic_topup || false);
  const [isUpdatingAutoRenew, setIsUpdatingAutoRenew] = useState(false);

  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);

  useEffect(() => {
    if (selectedEsim) {
      setIsAutoRenew(selectedEsim.automatic_topup || false);
    }
  }, [selectedEsim]);

  useEffect(() => {
    if (deviceType) {
      setLoadingDisable(true);

      fetchDisableInstructions(deviceType)
        .then((data) => {
          const prefix = deviceType.toUpperCase() === "IOS" ? "IOS" : deviceType.toLowerCase();
          const key = `${prefix}DisableSteps`;

          setDisableSteps(data?.[key] || []);
        })
        .catch((err) => console.error(err))
        .finally(() => setLoadingDisable(false));
    }
  }, [deviceType]);

  const handleAutoRenewChange = async (newValue) => {
    if (!selectedEsim?.iccid) return;

    const previousValue = isAutoRenew;
    setIsAutoRenew(newValue);
    setIsUpdatingAutoRenew(true);

    try {
      await updateAutoTopup(selectedEsim.iccid, newValue);
    } catch (error) {
      console.error("Failed to update auto-renew, rolling back.");
      setIsAutoRenew(previousValue);
    } finally {
      setIsUpdatingAutoRenew(false);
    }
  };

  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const renderStep = (step) => {
    return step.replace(/\*(.*?)\*/g, '<span style="font-weight:600">$1</span>');
  };

  const displayIccid = selectedEsim?.iccid ? selectedEsim.iccid.slice(-10) : "";

  return (
    <div className="esim-actions">
      <div className="esim-field">
        <div className="esim-id-section">
          <label>{t("esim.esimId")}</label>
          <div className="input-with-copy">
            <MainInput value={displayIccid} readOnly placeholder="0000000000" />
            <button
              className={`copy-btn ${copiedField === "iccid" ? "copied" : ""}`}
              onClick={() => copyToClipboard(displayIccid, "iccid")}
              title="Copy to clipboard"
            >
              <img src={copyIcon} alt="Copy" />
            </button>
          </div>
        </div>

        {hasTopUpPackages && (
          <div className="auto-renew-section">
            <div className="auto-renew-text">
              <label>{t("esim.autoRenewal")}</label>
              <p>{t("esim.autoRenewDesc")}</p>
            </div>
            <Switch
              checked={isAutoRenew}
              onChange={handleAutoRenewChange}
              disabled={isUpdatingAutoRenew || !selectedEsim}
            />
          </div>
        )}

        {hasTopUpPackages && (
          <div className="actions-buttons-row">
            <SecondButton onClick={() => setIsRefundModalOpen(true)}>{t("esim.requestRefund")}</SecondButton>
            <MainButton onClick={onToggleTopUp}>{isTopUpOpen ? t("esim.hideTopup") : t("profile.topUp")}</MainButton>
          </div>
        )}
        <div className="disable-section">
          <label>{t("esim.disableEsim")}</label>
          <div className="disable-instructions-container">
            {loadingDisable ? (
              <div className="loading-text">{t("common.loading")}</div>
            ) : disableSteps.length > 0 ? (
              <ol className="instruction-steps">
                {disableSteps.map((step, index) => (
                  <li key={`disable-${index}`} dangerouslySetInnerHTML={{ __html: renderStep(step) }} />
                ))}
              </ol>
            ) : (
              <div className="no-instructions">No instructions available</div>
            )}
          </div>
        </div>
        <div className="warning-card">
          <p>
            {t("esim.warningSingleInstall")}
          </p>
        </div>
        {!hasTopUpPackages && (
          <div className="actions-button-row">
            <SecondButton onClick={() => setIsRefundModalOpen(true)}>{t("esim.requestRefund")}</SecondButton>
          </div>
        )}
      </div>
      <RefundModal isOpen={isRefundModalOpen} onClose={() => setIsRefundModalOpen(false)} iccid={selectedEsim?.iccid} />
    </div>
  );
};

export default EsimActions;
